'use client'
import React, { useState } from 'react'
import CommonTaile from '../../Common/CommonTaile'
import { useTranslations } from 'next-intl'

type TabType = 'torque' | 'flow' | 'power'

// 自定义数字输入框组件，带上下三角形按钮
interface NumberInputProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  className?: string
  step?: number
}

const NumberInput: React.FC<NumberInputProps> = ({ 
  value, 
  onChange, 
  placeholder, 
  className = '',
  step = 1
}) => {
  const [isHovered, setIsHovered] = useState(false)

  const handleIncrement = () => {
    const num = parseFloat(value) || 0
    onChange((num + 1).toString())
  }

  const handleDecrement = () => {
    const num = parseFloat(value) || 0
    const newValue = num - 1
    onChange(newValue > 0 ? newValue.toString() : '0')
  }

  return (
    <div 
      className="group relative flex-1 w-full min-w-0"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <input
        type="number"
        step="any"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={`calculator-input w-full px-4 py-3 pr-10 max-md:py-2.5 max-md:text-base border-2 border-gray-200 rounded-lg bg-white focus:outline-none focus:border-mainColor transition-colors duration-200 min-w-0 ${className}`}
      />
      {/* 自定义上下三角形按钮：手机端常显，桌面端悬停显 */}
      <div className="absolute right-2 top-1/2 -translate-y-1/2 flex flex-col z-10 bg-white border border-gray-300 rounded shadow-sm max-md:flex md:opacity-0 md:group-hover:opacity-100 md:transition-opacity">
          <button
            type="button"
            onClick={handleIncrement}
            className="w-6 h-4 flex items-center justify-center cursor-pointer hover:bg-gray-200 rounded-t transition-colors active:bg-gray-300 touch-manipulation"
            aria-label="Increase"
          >
            <svg width="10" height="7" viewBox="0 0 10 7" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M5 0L10 7H0L5 0Z" fill="#4B5563" />
            </svg>
          </button>
          <div className="h-px bg-gray-300"></div>
          <button
            type="button"
            onClick={handleDecrement}
            className="w-6 h-4 flex items-center justify-center cursor-pointer hover:bg-gray-200 rounded-b transition-colors active:bg-gray-300 touch-manipulation"
            aria-label="Decrease"
          >
            <svg width="10" height="7" viewBox="0 0 10 7" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M5 7L0 0H10L5 7Z" fill="#4B5563" />
            </svg>
          </button>
        </div>
    </div>
  )
}

const CalculatorPage: React.FC = () => {
  const t = useTranslations('service')
  const [activeTab, setActiveTab] = useState<TabType>('torque')

  const breadcrumbItems = [
    { label: t("Home"), link: "/" },
    { label: t("calculator"), link: "" }
  ];

  // 扭矩计算状态
  const [motorDisplacement, setMotorDisplacement] = useState('')
  const [motorPressure, setMotorPressure] = useState('')
  const [motorTorqueResult, setMotorTorqueResult] = useState('')
  
  const [motorPower, setMotorPower] = useState('')
  const [motorSpeed, setMotorSpeed] = useState('')
  const [electricMotorTorqueResult, setElectricMotorTorqueResult] = useState('')

  // 流量计算状态
  const [flowMotorDisplacement, setFlowMotorDisplacement] = useState('')
  const [flowMotorSpeed, setFlowMotorSpeed] = useState('')
  const [flowMotorResult, setFlowMotorResult] = useState('')
  
  const [pumpDisplacement, setPumpDisplacement] = useState('')
  const [pumpSpeed, setPumpSpeed] = useState('')
  const [pumpFlowResult, setPumpFlowResult] = useState('')

  // 功率计算状态
  const [powerPumpDisplacement, setPowerPumpDisplacement] = useState('')
  const [powerPumpSpeed, setPowerPumpSpeed] = useState('')
  const [powerPumpPressure, setPowerPumpPressure] = useState('')
  const [powerPumpResult, setPowerPumpResult] = useState('')
  
  const [powerMotorTorque, setPowerMotorTorque] = useState('')
  const [powerMotorSpeed, setPowerMotorSpeed] = useState('')
  const [powerMotorResult, setPowerMotorResult] = useState('')

  // 格式化数字，保留更多小数位
  const formatNumber = (num: number): string => {
    if (num === 0) return '0'
    // 统一保留5位小数，确保精度
    const formatted = num.toFixed(5)
    // 去除末尾的0，但保留至少一位小数
    return formatted.replace(/\.?0+$/, '') || '0'
  }

  // 计算液压马达扭矩
  const calculateMotorTorque = () => {
    const displacement = parseFloat(motorDisplacement)
    const pressure = parseFloat(motorPressure)
    
    if (isNaN(displacement) || isNaN(pressure) || displacement <= 0 || pressure <= 0) {
      setMotorTorqueResult('')
      return
    }
    
    // 扭矩 = 排量*承压*0.159*0.85
    const torque = displacement * pressure * 0.159 * 0.85
    setMotorTorqueResult(formatNumber(torque))
  }

  // 计算电机扭矩
  const calculateElectricMotorTorque = () => {
    const power = parseFloat(motorPower)
    const speed = parseFloat(motorSpeed)
    
    if (isNaN(power) || isNaN(speed) || power <= 0 || speed <= 0) {
      setElectricMotorTorqueResult('')
      return
    }
    
    // 扭矩 = 功率*9550/转速
    const torque = power * 9550 / speed
    setElectricMotorTorqueResult(formatNumber(torque))
  }

  // 计算液压马达流量
  const calculateMotorFlow = () => {
    const speed = parseFloat(flowMotorSpeed)
    const displacement = parseFloat(flowMotorDisplacement)
    
    if (isNaN(speed) || isNaN(displacement) || speed <= 0 || displacement <= 0) {
      setFlowMotorResult('')
      return
    }
    
    // 流量 = 转速*马达排量/0.85
    const flow = speed * displacement / 0.85 / 1000 // 转换为 L/min
    setFlowMotorResult(formatNumber(flow))
  }

  // 计算液压油泵流量
  const calculatePumpFlow = () => {
    const speed = parseFloat(pumpSpeed)
    const displacement = parseFloat(pumpDisplacement)
    
    if (isNaN(speed) || isNaN(displacement) || speed <= 0 || displacement <= 0) {
      setPumpFlowResult('')
      return
    }
    
    // 流量 = 转速*排量
    const flow = speed * displacement / 1000 // 转换为 L/min
    setPumpFlowResult(formatNumber(flow))
  }

  // 计算液压油泵功率
  const calculatePumpPower = () => {
    const displacement = parseFloat(powerPumpDisplacement)
    const speed = parseFloat(powerPumpSpeed)
    const pressure = parseFloat(powerPumpPressure)
    
    if (isNaN(displacement) || isNaN(speed) || isNaN(pressure) || displacement <= 0 || speed <= 0 || pressure <= 0) {
      setPowerPumpResult('')
      return
    }
    
    // 先计算流量，然后计算功率
    // 流量 = 转速*排量 (L/min)
    const flow = speed * displacement / 1000
    // 功率 = 流量*承压/612
    // 注意：如果压力单位是 kg.f/cm²，需要转换为 MPA (MPA = 10kg.f/cm²)
    const pressureInMPA = pressure / 10 // 假设输入的是 kg.f/cm²
    const power = flow * pressureInMPA / 612
    setPowerPumpResult(formatNumber(power))
  }

  // 计算液压马达功率
  const calculateMotorPower = () => {
    const torque = parseFloat(powerMotorTorque)
    const speed = parseFloat(powerMotorSpeed)
    
    if (isNaN(torque) || isNaN(speed) || torque <= 0 || speed <= 0) {
      setPowerMotorResult('')
      return
    }
    
    // 功率 = 扭矩*转速/9550
    const power = torque * speed / 9550
    setPowerMotorResult(formatNumber(power))
  }

  return (
    <div>
      <CommonTaile 
        msg={t("calculator")} 
        breadcrumbItems={breadcrumbItems} 
        backgroundImage="/image/plsyy/calculator.webp" 
        pageSlug="service/calculator"
      />
      
      {/* 隐藏默认的 spinner 按钮 */}
      <style dangerouslySetInnerHTML={{__html: `
        /* 隐藏默认的 spinner 按钮 */
        .calculator-input[type="number"]::-webkit-inner-spin-button,
        .calculator-input[type="number"]::-webkit-outer-spin-button {
          -webkit-appearance: none !important;
          margin: 0 !important;
          display: none !important;
        }
        
        /* Firefox - 隐藏 spinner */
        .calculator-input[type="number"] {
          -moz-appearance: textfield !important;
        }
        
        /* 防止 focus 时布局抖动 */
        .calculator-input {
          box-sizing: border-box !important;
        }
        .calculator-input:focus {
          border-width: 2px !important;
          outline: none !important;
        }
      `}} />
      
      <div className="container mx-auto px-4 py-8 max-md:py-4 md:py-12">
        {/* 标签页导航 - 仅手机端纵向排列，桌面端保持原样 */}
        <div className="flex justify-center mb-10 max-md:mb-6">
          <div className="w-[80%] max-md:w-full bg-gray-100 rounded-full max-md:rounded-2xl p-1 max-md:p-1.5">
            <div className="flex flex-wrap max-md:flex-col max-md:gap-1">
              <button
                onClick={() => setActiveTab('torque')}
                className={`flex-1 min-w-[200px] max-md:min-w-0 max-md:w-full px-6 max-md:px-3 py-3 max-md:py-3 text-lg max-md:text-sm font-medium transition-all duration-300 rounded-tl-full rounded-bl-full max-md:rounded-xl ${
                  activeTab === 'torque'
                    ? 'bg-mainColor text-white'
                    : 'bg-transparent text-gray-800 hover:bg-gray-300'
                }`}
              >
                {t("torqueCalculator")}
              </button>
              <div className="w-px bg-gray-300 my-2 max-md:w-full max-md:h-px max-md:my-0 flex-shrink-0"></div>
              <button
                onClick={() => setActiveTab('flow')}
                className={`flex-1 min-w-[200px] max-md:min-w-0 max-md:w-full px-6 max-md:px-3 py-3 text-lg max-md:text-sm font-medium transition-all duration-300 max-md:rounded-xl ${
                  activeTab === 'flow'
                    ? 'bg-mainColor text-white'
                    : 'bg-transparent text-gray-800 hover:bg-gray-300'
                }`}
              >
                {t("flowCalculator")}
              </button>
              <div className="w-px bg-gray-300 my-2 max-md:w-full max-md:h-px max-md:my-0 flex-shrink-0"></div>
              <button
                onClick={() => setActiveTab('power')}
                className={`flex-1 min-w-[200px] max-md:min-w-0 max-md:w-full px-6 max-md:px-3 py-3 text-lg max-md:text-sm font-medium transition-all duration-300 rounded-tr-full rounded-br-full max-md:rounded-xl ${
                  activeTab === 'power'
                    ? 'bg-mainColor text-white'
                    : 'bg-transparent text-gray-800 hover:bg-gray-300'
                }`}
              >
                {t("powerCalculator")}
              </button>
            </div>
          </div>
        </div>

        {/* 扭矩计算器 */}
        {activeTab === 'torque' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-md:gap-4 lg:gap-12 relative">
            {/* 左侧：液压马达扭矩计算 */}
            <div className="bg-white p-10 max-md:p-4 transition-shadow duration-300 max-md:rounded-xl max-md:shadow-sm">
              <div className="mb-8 max-md:mb-4 pb-4 border-b-2 border-mainColor">
                <h2 className="text-3xl max-md:text-xl font-bold text-gray-900 mb-2 flex items-center gap-3">
                  <span className="w-1 h-8 bg-mainColor rounded-full flex-shrink-0"></span>
                  <span className="break-words">{t("motorTorqueCalc")}</span>
                </h2>
              </div>
              
              <div className="space-y-8 max-md:space-y-4">
                <div className="flex items-center gap-4 max-md:flex-col max-md:items-stretch max-md:gap-2">
                  <label className="text-gray-700 w-[120px] max-md:w-full flex-shrink-0 font-medium max-md:text-base">{t("motorDisplacement")}</label>
                  <div className="flex items-center gap-2 flex-1 min-w-0 max-md:w-full">
                    <NumberInput
                      value={motorDisplacement}
                      onChange={setMotorDisplacement}
                      placeholder={t("motorDisplacementPlaceholder")}
                      step={1}
                    />
                    <span className="text-gray-600 w-[80px] max-md:w-auto flex-shrink-0 text-left font-medium">(ml/r)</span>
                  </div>
                </div>
                
                <div className="flex items-center gap-4 max-md:flex-col max-md:items-stretch max-md:gap-2">
                  <label className="text-gray-700 w-[120px] max-md:w-full flex-shrink-0 max-md:text-base">{t("motorPressure")}</label>
                  <div className="flex items-center gap-2 flex-1 min-w-0 max-md:w-full">
                    <NumberInput
                      value={motorPressure}
                      onChange={setMotorPressure}
                      placeholder={t("motorPressurePlaceholder")}
                      step={0.1}
                    />
                    <span className="text-gray-600 w-[80px] max-md:w-auto flex-shrink-0 text-left font-medium">(MPA)</span>
                  </div>
                </div>
                
                <div className="flex items-center gap-4 max-md:flex-col max-md:items-stretch max-md:gap-2">
                  <label className="text-gray-700 w-[120px] max-md:w-full flex-shrink-0 font-medium max-md:text-base">{t("calculationResult")}</label>
                  <div className="flex items-center gap-2 flex-1 min-w-0 max-md:w-full">
                    <input
                      type="text"
                      value={motorTorqueResult}
                      readOnly
                      tabIndex={-1}
                      className="flex-1 min-w-0 w-full px-4 py-3 max-md:py-2.5 border-2 border-gray-200 rounded-lg bg-gray-50 font-semibold text-gray-800 cursor-text select-text"
                    />
                    <span className="text-gray-600 w-[80px] max-md:w-auto flex-shrink-0 text-left font-medium">(N.M)</span>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center gap-4 max-md:flex-col max-md:gap-2 mt-8 max-md:mt-4">
                <div className="w-[120px] max-md:hidden flex-shrink-0"></div>
                <button
                  onClick={calculateMotorTorque}
                  className="flex-1 w-full bg-mainColor text-white py-3 max-md:py-2.5 rounded-lg font-semibold hover:bg-mainColor/90 transition-all duration-300 shadow-md hover:shadow-lg transform hover:-translate-y-0.5 max-md:active:scale-[0.98]"
                >
                  {t("clickCalculate")}
                </button>
                <div className="w-[80px] max-md:hidden flex-shrink-0"></div>
              </div>
              
              <div className="mt-10 max-md:mt-6 p-5 max-md:p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border-l-4 border-mainColor shadow-sm">
                <p className="text-sm font-medium text-gray-800 leading-relaxed max-md:text-xs">
                  {t("motorTorqueFormula")}
                </p>
              </div>
            </div>

            {/* 中间分割虚线 */}
            <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-px border-l-2 border-dashed border-gray-300 -translate-x-1/2"></div>

            {/* 右侧：电机扭矩计算 */}
            <div className="bg-white p-10 max-md:p-4 transition-shadow duration-300 max-md:rounded-xl max-md:shadow-sm">
              <div className="mb-8 max-md:mb-4 pb-4 border-b-2 border-mainColor">
                <h2 className="text-3xl max-md:text-xl font-bold text-gray-900 mb-2 flex items-center gap-3">
                  <span className="w-1 h-8 bg-mainColor rounded-full flex-shrink-0"></span>
                  <span className="break-words">{t("electricMotorTorqueCalc")}</span>
                </h2>
              </div>
              
              <div className="space-y-8 max-md:space-y-4">
                <div className="flex items-center gap-4 max-md:flex-col max-md:items-stretch max-md:gap-2">
                  <label className="text-gray-700 w-[120px] max-md:w-full flex-shrink-0 max-md:text-base">{t("motorPower")}</label>
                  <div className="flex items-center gap-2 flex-1 min-w-0 max-md:w-full">
                    <NumberInput
                      value={motorPower}
                      onChange={setMotorPower}
                      placeholder={t("motorPowerPlaceholder")}
                      step={0.1}
                    />
                    <span className="text-gray-600 w-[80px] max-md:w-auto flex-shrink-0 text-left font-medium">(kw)</span>
                  </div>
                </div>
                
                <div className="flex items-center gap-4 max-md:flex-col max-md:items-stretch max-md:gap-2">
                  <label className="text-gray-700 w-[120px] max-md:w-full flex-shrink-0 max-md:text-base">{t("motorSpeed")}</label>
                  <div className="flex items-center gap-2 flex-1 min-w-0 max-md:w-full">
                    <NumberInput
                      value={motorSpeed}
                      onChange={setMotorSpeed}
                      placeholder={t("motorSpeedPlaceholder")}
                      step={10}
                    />
                    <span className="text-gray-600 w-[80px] max-md:w-auto flex-shrink-0 text-left font-medium">(r/min)</span>
                  </div>
                </div>
                
                <div className="flex items-center gap-4 max-md:flex-col max-md:items-stretch max-md:gap-2">
                  <label className="text-gray-700 w-[120px] max-md:w-full flex-shrink-0 font-medium max-md:text-base">{t("calculationResult")}</label>
                  <div className="flex items-center gap-2 flex-1 min-w-0 max-md:w-full">
                    <input
                      type="text"
                      value={electricMotorTorqueResult}
                      readOnly
                      tabIndex={-1}
                      className="flex-1 min-w-0 w-full px-4 py-3 max-md:py-2.5 border-2 border-gray-200 rounded-lg bg-gray-50 font-semibold text-gray-800 cursor-text select-text"
                    />
                    <span className="text-gray-600 w-[80px] max-md:w-auto flex-shrink-0 text-left font-medium">(N.M)</span>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center gap-4 max-md:flex-col max-md:gap-2 mt-8 max-md:mt-4">
                <div className="w-[120px] max-md:hidden flex-shrink-0"></div>
                <button
                  onClick={calculateElectricMotorTorque}
                  className="flex-1 w-full bg-mainColor text-white py-3 max-md:py-2.5 rounded-lg font-medium hover:bg-mainColor/90 transition-colors duration-300 max-md:active:scale-[0.98]"
                >
                  {t("clickCalculate")}
                </button>
                <div className="w-[80px] max-md:hidden flex-shrink-0"></div>
              </div>
              
              <div className="mt-10 max-md:mt-6 p-5 max-md:p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border-l-4 border-mainColor shadow-sm">
                <p className="text-sm font-medium text-gray-800 leading-relaxed max-md:text-xs">
                  {t("electricMotorTorqueFormula")}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* 流量计算器 */}
        {activeTab === 'flow' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-md:gap-4 lg:gap-12 relative">
            {/* 左侧：液压马达流量计算 */}
            <div className="bg-white p-10 max-md:p-4 transition-shadow duration-300 max-md:rounded-xl max-md:shadow-sm">
              <div className="mb-8 max-md:mb-4 pb-4 border-b-2 border-mainColor">
                <h2 className="text-3xl max-md:text-xl font-bold text-gray-900 mb-2 flex items-center gap-3">
                  <span className="w-1 h-8 bg-mainColor rounded-full flex-shrink-0"></span>
                  <span className="break-words">{t("motorFlowCalc")}</span>
                </h2>
              </div>
              
              <div className="space-y-8 max-md:space-y-4">
                <div className="flex items-center gap-4 max-md:flex-col max-md:items-stretch max-md:gap-2">
                  <label className="text-gray-700 w-[120px] max-md:w-full flex-shrink-0 font-medium max-md:text-base">{t("motorDisplacement")}</label>
                  <div className="flex items-center gap-2 flex-1 min-w-0 max-md:w-full">
                    <NumberInput
                      value={flowMotorDisplacement}
                      onChange={setFlowMotorDisplacement}
                      placeholder={t("motorDisplacementPlaceholder")}
                      step={1}
                    />
                    <span className="text-gray-600 w-[80px] max-md:w-auto flex-shrink-0 text-left font-medium">(ml/r)</span>
                  </div>
                </div>
                
                <div className="flex items-center gap-4 max-md:flex-col max-md:items-stretch max-md:gap-2">
                  <label className="text-gray-700 w-[120px] max-md:w-full flex-shrink-0 max-md:text-base">{t("motorSpeed")}</label>
                  <div className="flex items-center gap-2 flex-1 min-w-0 max-md:w-full">
                    <NumberInput
                      value={flowMotorSpeed}
                      onChange={setFlowMotorSpeed}
                      placeholder={t("motorFlowSpeedPlaceholder")}
                      step={10}
                    />
                    <span className="text-gray-600 w-[80px] max-md:w-auto flex-shrink-0 text-left font-medium">(r/min)</span>
                  </div>
                </div>
                
                <div className="flex items-center gap-4 max-md:flex-col max-md:items-stretch max-md:gap-2">
                  <label className="text-gray-700 w-[120px] max-md:w-full flex-shrink-0 font-medium max-md:text-base">{t("calculationResult")}</label>
                  <div className="flex items-center gap-2 flex-1 min-w-0 max-md:w-full">
                    <input
                      type="text"
                      value={flowMotorResult}
                      readOnly
                      tabIndex={-1}
                      className="flex-1 min-w-0 w-full px-4 py-3 max-md:py-2.5 border-2 border-gray-200 rounded-lg bg-gray-50 font-semibold text-gray-800 cursor-text select-text"
                    />
                    <span className="text-gray-600 w-[80px] max-md:w-auto flex-shrink-0 text-left font-medium">(L/min)</span>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center gap-4 max-md:flex-col max-md:gap-2 mt-8 max-md:mt-4">
                <div className="w-[120px] max-md:hidden flex-shrink-0"></div>
                <button
                  onClick={calculateMotorFlow}
                  className="flex-1 w-full bg-mainColor text-white py-3 max-md:py-2.5 rounded-lg font-medium hover:bg-mainColor/90 transition-colors duration-300 max-md:active:scale-[0.98]"
                >
                  {t("clickCalculate")}
                </button>
                <div className="w-[80px] max-md:hidden flex-shrink-0"></div>
              </div>
              
              <div className="mt-10 max-md:mt-6 p-5 max-md:p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border-l-4 border-mainColor shadow-sm">
                <p className="text-sm font-medium text-gray-800 leading-relaxed max-md:text-xs">
                  {t("flowFormula")}
                </p>
              </div>
            </div>

            {/* 中间分割虚线 */}
            <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-px border-l-2 border-dashed border-gray-300 -translate-x-1/2"></div>

            {/* 右侧：液压油泵流量计算 */}
            <div className="bg-white p-10 max-md:p-4 transition-shadow duration-300 max-md:rounded-xl max-md:shadow-sm">
              <div className="mb-8 max-md:mb-4 pb-4 border-b-2 border-mainColor">
                <h2 className="text-3xl max-md:text-xl font-bold text-gray-900 mb-2 flex items-center gap-3">
                  <span className="w-1 h-8 bg-mainColor rounded-full flex-shrink-0"></span>
                  <span className="break-words">{t("pumpFlowCalc")}</span>
                </h2>
              </div>
              
              <div className="space-y-8 max-md:space-y-4">
                <div className="flex items-center gap-4 max-md:flex-col max-md:items-stretch max-md:gap-2">
                  <label className="text-gray-700 w-[120px] max-md:w-full flex-shrink-0 max-md:text-base">{t("pumpDisplacement")}</label>
                  <div className="flex items-center gap-2 flex-1 min-w-0 max-md:w-full">
                    <NumberInput
                      value={pumpDisplacement}
                      onChange={setPumpDisplacement}
                      placeholder={t("pumpDisplacementPlaceholder")}
                      step={1}
                    />
                    <span className="text-gray-600 w-[80px] max-md:w-auto flex-shrink-0 text-left font-medium">(ml/r)</span>
                  </div>
                </div>
                
                <div className="flex items-center gap-4 max-md:flex-col max-md:items-stretch max-md:gap-2">
                  <label className="text-gray-700 w-[120px] max-md:w-full flex-shrink-0 max-md:text-base">{t("pumpSpeed")}</label>
                  <div className="flex items-center gap-2 flex-1 min-w-0 max-md:w-full">
                    <NumberInput
                      value={pumpSpeed}
                      onChange={setPumpSpeed}
                      placeholder={t("pumpSpeedPlaceholder")}
                      step={10}
                    />
                    <span className="text-gray-600 w-[80px] max-md:w-auto flex-shrink-0 text-left font-medium">(r/min)</span>
                  </div>
                </div>
                
                <div className="flex items-center gap-4 max-md:flex-col max-md:items-stretch max-md:gap-2">
                  <label className="text-gray-700 w-[120px] max-md:w-full flex-shrink-0 font-medium max-md:text-base">{t("calculationResult")}</label>
                  <div className="flex items-center gap-2 flex-1 min-w-0 max-md:w-full">
                    <input
                      type="text"
                      value={pumpFlowResult}
                      readOnly
                      tabIndex={-1}
                      className="flex-1 min-w-0 w-full px-4 py-3 max-md:py-2.5 border-2 border-gray-200 rounded-lg bg-gray-50 font-semibold text-gray-800 cursor-text select-text"
                    />
                    <span className="text-gray-600 w-[80px] max-md:w-auto flex-shrink-0 text-left font-medium">(L/min)</span>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center gap-4 max-md:flex-col max-md:gap-2 mt-8 max-md:mt-4">
                <div className="w-[120px] max-md:hidden flex-shrink-0"></div>
                <button
                  onClick={calculatePumpFlow}
                  className="flex-1 w-full bg-mainColor text-white py-3 max-md:py-2.5 rounded-lg font-medium hover:bg-mainColor/90 transition-colors duration-300 max-md:active:scale-[0.98]"
                >
                  {t("clickCalculate")}
                </button>
                <div className="w-[80px] max-md:hidden flex-shrink-0"></div>
              </div>
              
              <div className="mt-10 max-md:mt-6 p-5 max-md:p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border-l-4 border-mainColor shadow-sm">
                <p className="text-sm font-medium text-gray-800 leading-relaxed max-md:text-xs">
                  {t("flowFormula")}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* 功率计算器 */}
        {activeTab === 'power' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-md:gap-4 lg:gap-12 relative">
            {/* 左侧：液压油泵功率计算 */}
            <div className="bg-white p-10 max-md:p-4 transition-shadow duration-300 max-md:rounded-xl max-md:shadow-sm">
              <div className="mb-8 max-md:mb-4 pb-4 border-b-2 border-mainColor">
                <h2 className="text-3xl max-md:text-xl font-bold text-gray-900 mb-2 flex items-center gap-3">
                  <span className="w-1 h-8 bg-mainColor rounded-full flex-shrink-0"></span>
                  <span className="break-words">{t("pumpPowerCalc")}</span>
                </h2>
              </div>
              
              <div className="space-y-8 max-md:space-y-4">
                <div className="flex items-center gap-4 max-md:flex-col max-md:items-stretch max-md:gap-2">
                  <label className="text-gray-700 w-[120px] max-md:w-full flex-shrink-0 max-md:text-base">{t("pumpDisplacement")}</label>
                  <div className="flex items-center gap-2 flex-1 min-w-0 max-md:w-full">
                    <NumberInput
                      value={powerPumpDisplacement}
                      onChange={setPowerPumpDisplacement}
                      placeholder={t("pumpPowerDisplacementPlaceholder")}
                      step={1}
                    />
                    <span className="text-gray-600 w-[80px] max-md:w-auto flex-shrink-0 text-left font-medium">(ml/r)</span>
                  </div>
                </div>
                
                <div className="flex items-center gap-4 max-md:flex-col max-md:items-stretch max-md:gap-2">
                  <label className="text-gray-700 w-[120px] max-md:w-full flex-shrink-0 max-md:text-base">{t("pumpSpeed")}</label>
                  <div className="flex items-center gap-2 flex-1 min-w-0 max-md:w-full">
                    <NumberInput
                      value={powerPumpSpeed}
                      onChange={setPowerPumpSpeed}
                      placeholder={t("pumpPowerSpeedPlaceholder")}
                      step={10}
                    />
                    <span className="text-gray-600 w-[80px] max-md:w-auto flex-shrink-0 text-left font-medium">(r/min)</span>
                  </div>
                </div>
                
                <div className="flex items-center gap-4 max-md:flex-col max-md:items-stretch max-md:gap-2">
                  <label className="text-gray-700 w-[120px] max-md:w-full flex-shrink-0 max-md:text-base">{t("pumpPressure")}</label>
                  <div className="flex items-center gap-2 flex-1 min-w-0 max-md:w-full">
                    <NumberInput
                      value={powerPumpPressure}
                      onChange={setPowerPumpPressure}
                      placeholder={t("pumpPressurePlaceholder")}
                      step={0.1}
                    />
                    <span className="text-gray-600 w-[80px] max-md:w-auto flex-shrink-0 text-left font-medium">(kg.f/cm²)</span>
                  </div>
                </div>
                
                <div className="flex items-center gap-4 max-md:flex-col max-md:items-stretch max-md:gap-2">
                  <label className="text-gray-700 w-[120px] max-md:w-full flex-shrink-0 font-medium max-md:text-base">{t("calculationResult")}</label>
                  <div className="flex items-center gap-2 flex-1 min-w-0 max-md:w-full">
                    <input
                      type="text"
                      value={powerPumpResult}
                      readOnly
                      tabIndex={-1}
                      className="flex-1 min-w-0 w-full px-4 py-3 max-md:py-2.5 border-2 border-gray-200 rounded-lg bg-gray-50 font-semibold text-gray-800 cursor-text select-text"
                    />
                    <span className="text-gray-600 w-[80px] max-md:w-auto flex-shrink-0 text-left font-medium">(KW)</span>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center gap-4 max-md:flex-col max-md:gap-2 mt-8 max-md:mt-4">
                <div className="w-[120px] max-md:hidden flex-shrink-0"></div>
                <button
                  onClick={calculatePumpPower}
                  className="flex-1 w-full bg-mainColor text-white py-3 max-md:py-2.5 rounded-lg font-medium hover:bg-mainColor/90 transition-colors duration-300 max-md:active:scale-[0.98]"
                >
                  {t("clickCalculate")}
                </button>
                <div className="w-[80px] max-md:hidden flex-shrink-0"></div>
              </div>
              
              <div className="mt-10 max-md:mt-6 p-5 max-md:p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border-l-4 border-mainColor shadow-sm">
                <p className="text-sm font-medium text-gray-800 leading-relaxed max-md:text-xs">
                  {t("pumpPowerFormula")}
                </p>
              </div>
            </div>

            {/* 中间分割虚线 */}
            <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-px border-l-2 border-dashed border-gray-300 -translate-x-1/2"></div>

            {/* 右侧：液压马达功率计算 */}
            <div className="bg-white p-10 max-md:p-4 transition-shadow duration-300 max-md:rounded-xl max-md:shadow-sm">
              <div className="mb-8 max-md:mb-4 pb-4 border-b-2 border-mainColor">
                <h2 className="text-3xl max-md:text-xl font-bold text-gray-900 mb-2 flex items-center gap-3">
                  <span className="w-1 h-8 bg-mainColor rounded-full flex-shrink-0"></span>
                  <span className="break-words">{t("motorPowerCalc")}</span>
                </h2>
              </div>
              
              <div className="space-y-8 max-md:space-y-4">
                <div className="flex items-center gap-4 max-md:flex-col max-md:items-stretch max-md:gap-2">
                  <label className="text-gray-700 w-[120px] max-md:w-full flex-shrink-0 max-md:text-base">{t("motorTorque")}</label>
                  <div className="flex items-center gap-2 flex-1 min-w-0 max-md:w-full">
                    <NumberInput
                      value={powerMotorTorque}
                      onChange={setPowerMotorTorque}
                      placeholder={t("motorTorquePlaceholder")}
                      step={0.1}
                    />
                    <span className="text-gray-600 w-[80px] max-md:w-auto flex-shrink-0 text-left font-medium">(N.M)</span>
                  </div>
                </div>
                
                <div className="flex items-center gap-4 max-md:flex-col max-md:items-stretch max-md:gap-2">
                  <label className="text-gray-700 w-[120px] max-md:w-full flex-shrink-0 max-md:text-base">{t("motorSpeed")}</label>
                  <div className="flex items-center gap-2 flex-1 min-w-0 max-md:w-full">
                    <NumberInput
                      value={powerMotorSpeed}
                      onChange={setPowerMotorSpeed}
                      placeholder={t("motorPowerSpeedPlaceholder")}
                      step={10}
                    />
                    <span className="text-gray-600 w-[80px] max-md:w-auto flex-shrink-0 text-left font-medium">(r/min)</span>
                  </div>
                </div>
                
                <div className="flex items-center gap-4 max-md:flex-col max-md:items-stretch max-md:gap-2">
                  <label className="text-gray-700 w-[120px] max-md:w-full flex-shrink-0 font-medium max-md:text-base">{t("calculationResult")}</label>
                  <div className="flex items-center gap-2 flex-1 min-w-0 max-md:w-full">
                    <input
                      type="text"
                      value={powerMotorResult}
                      readOnly
                      tabIndex={-1}
                      className="flex-1 min-w-0 w-full px-4 py-3 max-md:py-2.5 border-2 border-gray-200 rounded-lg bg-gray-50 font-semibold text-gray-800 cursor-text select-text"
                    />
                    <span className="text-gray-600 w-[80px] max-md:w-auto flex-shrink-0 text-left font-medium">(KW)</span>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center gap-4 max-md:flex-col max-md:gap-2 mt-8 max-md:mt-4">
                <div className="w-[120px] max-md:hidden flex-shrink-0"></div>
                <button
                  onClick={calculateMotorPower}
                  className="flex-1 w-full bg-mainColor text-white py-3 max-md:py-2.5 rounded-lg font-medium hover:bg-mainColor/90 transition-colors duration-300 max-md:active:scale-[0.98]"
                >
                  {t("clickCalculate")}
                </button>
                <div className="w-[80px] max-md:hidden flex-shrink-0"></div>
              </div>
              
              <div className="mt-10 max-md:mt-6 p-5 max-md:p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border-l-4 border-mainColor shadow-sm">
                <p className="text-sm font-medium text-gray-800 leading-relaxed max-md:text-xs">
                  {t("motorPowerFormula")}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default CalculatorPage
